import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  LayoutDashboard, Search, Download, LogOut, AlertTriangle, DollarSign, ClipboardList,
  Loader2, X, Bug, MapPin, Phone, Mail, Calendar, User, FileImage, Save, Home,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { api, API } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { LOGO_URL } from "@/data/site";
import { STATUS_COLORS } from "@/pages/Portal";

const STATUSES = ["New Request", "Inspection Scheduled", "Inspection Complete", "Estimate Sent", "Treatment Scheduled", "Treatment Completed", "Follow-up Required", "Closed"];

export default function AdminDashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [selected, setSelected] = useState(null);
  const [techs, setTechs] = useState([]);
  const [edit, setEdit] = useState({});
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [s, list] = await Promise.all([
        api.get("/admin/stats"),
        api.get("/admin/inspections", { params: { search, status: statusFilter } }),
      ]);
      setStats(s.data); setItems(list.data);
    } catch { toast.error("Failed to load dashboard."); }
    finally { setLoading(false); }
  }, [search, statusFilter]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { api.get("/admin/technicians").then((r) => setTechs(r.data.technicians)).catch(() => {}); }, []);

  const openDetail = (it) => {
    setSelected(it);
    setEdit({
      status: it.status, assigned_technician: it.assigned_technician || "",
      scheduled_date: it.scheduled_date || "", estimate_amount: it.estimate_amount ?? "",
      estimate_note: it.estimate_note || "", invoice_amount: it.invoice_amount ?? "",
      invoice_status: it.invoice_status || "", admin_note: it.admin_note || "", treatment_note: "",
    });
  };

  const save = async () => {
    setSaving(true);
    try {
      const payload = {};
      ["status", "assigned_technician", "scheduled_date", "estimate_note", "invoice_status", "admin_note"].forEach((k) => { if (edit[k] !== "") payload[k] = edit[k]; });
      if (edit.estimate_amount !== "") payload.estimate_amount = parseFloat(edit.estimate_amount);
      if (edit.invoice_amount !== "") payload.invoice_amount = parseFloat(edit.invoice_amount);
      if (edit.treatment_note) payload.treatment_note = edit.treatment_note;
      const res = await api.patch(`/admin/inspections/${selected.id}`, payload);
      toast.success("Request updated.");
      setSelected(res.data); setEdit((e) => ({ ...e, treatment_note: "" }));
      load();
    } catch { toast.error("Update failed."); }
    finally { setSaving(false); }
  };

  const exportCsv = async () => {
    try {
      const res = await api.get("/admin/export", { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a"); a.href = url; a.download = "exora_inspections.csv"; a.click();
      URL.revokeObjectURL(url);
      toast.success("Report exported.");
    } catch { toast.error("Export failed."); }
  };

  const STAT_CARDS = stats ? [
    { label: "Total Requests", value: stats.total, icon: ClipboardList, color: "text-brand-green" },
    { label: "Active Jobs", value: (stats.total - (stats.by_status["Closed"] || 0)), icon: LayoutDashboard, color: "text-blue-600" },
    { label: "Emergencies", value: stats.emergency, icon: AlertTriangle, color: "text-red-600" },
    { label: "Invoiced Revenue", value: `$${(stats.revenue || 0).toLocaleString()}`, icon: DollarSign, color: "text-brand-gold" },
  ] : [];

  return (
    <div className="min-h-screen bg-brand-light">
      {/* Topbar */}
      <header className="bg-brand-green sticky top-0 z-40">
        <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="bg-white rounded-lg p-1.5"><img src={LOGO_URL} alt="EXORA" className="h-7" /></div>
            <span className="text-white font-heading font-bold hidden sm:block">Admin Dashboard</span>
          </div>
          <div className="flex items-center gap-3">
            <span className="text-white/70 text-sm hidden sm:block">{user?.email}</span>
            <Button size="sm" variant="ghost" onClick={() => navigate("/")} className="rounded-full text-white hover:bg-white/10"><Home className="h-4 w-4" /></Button>
            <Button size="sm" variant="outline" onClick={logout} data-testid="admin-logout-btn" className="rounded-full bg-white/10 text-white border-white/20 hover:bg-white/20"><LogOut className="h-4 w-4 mr-1.5" /> Logout</Button>
          </div>
        </div>
      </header>

      <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Stats */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {STAT_CARDS.map((c) => (
            <div key={c.label} className="bg-white rounded-2xl p-5 border border-black/5">
              <div className="flex items-center justify-between"><span className="text-brand-muted text-sm">{c.label}</span><c.icon className={`h-5 w-5 ${c.color}`} /></div>
              <p className={`font-heading text-3xl font-extrabold mt-2 ${c.color}`}>{loading ? "—" : c.value}</p>
            </div>
          ))}
        </div>

        {/* Toolbar */}
        <div className="flex flex-col sm:flex-row gap-3 mt-8 mb-4">
          <div className="relative flex-1">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-brand-muted" />
            <Input data-testid="admin-search" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search name, email, phone, reference, address..." className="pl-11 h-11 rounded-xl bg-white" />
          </div>
          <Select value={statusFilter || "all"} onValueChange={(v) => setStatusFilter(v === "all" ? "" : v)}>
            <SelectTrigger data-testid="admin-status-filter" className="w-full sm:w-56 h-11 rounded-xl bg-white"><SelectValue placeholder="All statuses" /></SelectTrigger>
            <SelectContent><SelectItem value="all">All Statuses</SelectItem>{STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
          </Select>
          <Button data-testid="admin-export-btn" onClick={exportCsv} className="rounded-xl bg-brand-green text-white hover:bg-brand-greenHover h-11"><Download className="h-4 w-4 mr-2" /> Export</Button>
        </div>

        {/* Table */}
        <div className="bg-white rounded-2xl border border-black/5 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="admin-table">
              <thead className="bg-brand-light border-b border-black/5">
                <tr className="text-left text-brand-muted">
                  {["Reference", "Customer", "Pest / Severity", "Property", "Status", "Technician", "Date"].map((h) => <th key={h} className="px-4 py-3 font-semibold whitespace-nowrap">{h}</th>)}
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr><td colSpan={7} className="text-center py-16"><Loader2 className="h-6 w-6 animate-spin text-brand-green mx-auto" /></td></tr>
                ) : items.length === 0 ? (
                  <tr><td colSpan={7} className="text-center py-16 text-brand-muted">No inspection requests found.</td></tr>
                ) : items.map((it) => (
                  <tr key={it.id} data-testid={`admin-row-${it.reference_number}`} onClick={() => openDetail(it)} className="border-b border-black/5 hover:bg-brand-light cursor-pointer transition-colors">
                    <td className="px-4 py-3 font-semibold text-brand-green whitespace-nowrap">{it.reference_number}</td>
                    <td className="px-4 py-3"><div className="font-medium text-brand-charcoal">{it.full_name}</div><div className="text-brand-muted text-xs">{it.email}</div></td>
                    <td className="px-4 py-3 whitespace-nowrap">{it.pest_type} {it.severity === "Emergency" && <Badge className="bg-red-100 text-red-700 border-0 ml-1">Emergency</Badge>}<div className="text-brand-muted text-xs">{it.severity}</div></td>
                    <td className="px-4 py-3 text-brand-muted">{it.property_type}</td>
                    <td className="px-4 py-3"><Badge className={`${STATUS_COLORS[it.status]} border-0 rounded-full whitespace-nowrap`}>{it.status}</Badge></td>
                    <td className="px-4 py-3 text-brand-muted whitespace-nowrap">{it.assigned_technician || "—"}</td>
                    <td className="px-4 py-3 text-brand-muted whitespace-nowrap">{new Date(it.created_at).toLocaleDateString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Detail Drawer */}
      <Dialog open={!!selected} onOpenChange={(o) => !o && setSelected(null)}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          {selected && (
            <>
              <DialogHeader>
                <DialogTitle className="font-heading text-brand-green flex items-center gap-3">
                  {selected.reference_number}
                  <Badge className={`${STATUS_COLORS[selected.status]} border-0 rounded-full`}>{selected.status}</Badge>
                </DialogTitle>
              </DialogHeader>

              <div className="grid md:grid-cols-2 gap-4 mt-2">
                <div className="bg-brand-light rounded-xl p-4 space-y-2 text-sm">
                  <p className="font-semibold text-brand-green flex items-center gap-2"><User className="h-4 w-4" /> Customer</p>
                  <p className="text-brand-charcoal">{selected.full_name}</p>
                  <p className="flex items-center gap-2 text-brand-muted"><Mail className="h-3.5 w-3.5" />{selected.email}</p>
                  <p className="flex items-center gap-2 text-brand-muted"><Phone className="h-3.5 w-3.5" />{selected.phone}</p>
                  <p className="flex items-center gap-2 text-brand-muted"><MapPin className="h-3.5 w-3.5" />{selected.address}</p>
                </div>
                <div className="bg-brand-light rounded-xl p-4 space-y-2 text-sm">
                  <p className="font-semibold text-brand-green flex items-center gap-2"><Bug className="h-4 w-4" /> Problem</p>
                  <p className="text-brand-charcoal">{selected.pest_type} · {selected.severity}</p>
                  <p className="text-brand-muted">Property: {selected.property_type}</p>
                  <p className="flex items-center gap-2 text-brand-muted"><Calendar className="h-3.5 w-3.5" />Preferred: {selected.preferred_date || "—"} {selected.preferred_time}</p>
                </div>
              </div>

              {selected.property_details && Object.values(selected.property_details).some(Boolean) && (
                <div className="bg-brand-light rounded-xl p-4 text-sm mt-1">
                  <p className="font-semibold text-brand-green mb-2">Property Details</p>
                  <div className="grid sm:grid-cols-2 gap-x-6 gap-y-1 text-brand-muted">
                    {Object.entries(selected.property_details).filter(([, v]) => v).map(([k, v]) => (
                      <div key={k} className="flex justify-between gap-2"><span className="capitalize">{k.replace(/_/g, " ")}</span><span className="text-brand-charcoal font-medium text-right">{v}</span></div>
                    ))}
                  </div>
                </div>
              )}

              {selected.photos?.length > 0 && (
                <div>
                  <p className="font-semibold text-brand-green text-sm flex items-center gap-2 mb-2"><FileImage className="h-4 w-4" /> Uploaded Photos ({selected.photos.length})</p>
                  <div className="grid grid-cols-4 gap-2" data-testid="admin-photos">
                    {selected.photos.map((p) => (
                      <a key={p.id} href={`${API}/files/${p.storage_path}`} target="_blank" rel="noreferrer" className="block rounded-lg overflow-hidden border border-black/5 bg-brand-light aspect-square">
                        {p.content_type?.startsWith("image/") ? <img src={`${API}/files/${p.storage_path}`} alt={p.original_filename} className="w-full h-full object-cover" />
                          : <div className="w-full h-full flex items-center justify-center text-brand-muted"><FileImage className="h-6 w-6" /></div>}
                      </a>
                    ))}
                  </div>
                </div>
              )}

              {/* Management */}
              <div className="border-t border-black/5 pt-4 mt-2">
                <p className="font-heading font-bold text-brand-green mb-3">Manage Job</p>
                <div className="grid sm:grid-cols-2 gap-4">
                  <div><Label className="text-xs">Status</Label>
                    <Select value={edit.status} onValueChange={(v) => setEdit({ ...edit, status: v })}>
                      <SelectTrigger data-testid="admin-edit-status" className="mt-1 h-10 rounded-lg"><SelectValue /></SelectTrigger>
                      <SelectContent>{STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div><Label className="text-xs">Assign Technician</Label>
                    <Select value={edit.assigned_technician || "none"} onValueChange={(v) => setEdit({ ...edit, assigned_technician: v === "none" ? "" : v })}>
                      <SelectTrigger data-testid="admin-edit-tech" className="mt-1 h-10 rounded-lg"><SelectValue placeholder="Unassigned" /></SelectTrigger>
                      <SelectContent><SelectItem value="none">Unassigned</SelectItem>{techs.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div><Label className="text-xs">Scheduled Date</Label><Input data-testid="admin-edit-date" type="date" value={edit.scheduled_date} onChange={(e) => setEdit({ ...edit, scheduled_date: e.target.value })} className="mt-1 h-10 rounded-lg" /></div>
                  <div className="grid grid-cols-2 gap-2">
                    <div><Label className="text-xs">Estimate $</Label><Input data-testid="admin-edit-estimate" type="number" value={edit.estimate_amount} onChange={(e) => setEdit({ ...edit, estimate_amount: e.target.value })} className="mt-1 h-10 rounded-lg" /></div>
                    <div><Label className="text-xs">Invoice $</Label><Input data-testid="admin-edit-invoice" type="number" value={edit.invoice_amount} onChange={(e) => setEdit({ ...edit, invoice_amount: e.target.value })} className="mt-1 h-10 rounded-lg" /></div>
                  </div>
                  <div><Label className="text-xs">Invoice Status</Label>
                    <Select value={edit.invoice_status || "none"} onValueChange={(v) => setEdit({ ...edit, invoice_status: v === "none" ? "" : v })}>
                      <SelectTrigger className="mt-1 h-10 rounded-lg"><SelectValue placeholder="—" /></SelectTrigger>
                      <SelectContent><SelectItem value="none">—</SelectItem>{["Pending", "Sent", "Paid"].map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div><Label className="text-xs">Estimate Note</Label><Input value={edit.estimate_note} onChange={(e) => setEdit({ ...edit, estimate_note: e.target.value })} className="mt-1 h-10 rounded-lg" placeholder="Scope / pricing note" /></div>
                </div>
                <div className="mt-4"><Label className="text-xs">Add Treatment / Job Note (visible to customer)</Label><Textarea data-testid="admin-treatment-note" value={edit.treatment_note} onChange={(e) => setEdit({ ...edit, treatment_note: e.target.value })} className="mt-1 rounded-lg min-h-[70px]" placeholder="e.g. Applied perimeter barrier treatment, next visit in 30 days." /></div>

                {selected.treatment_history?.length > 0 && (
                  <div className="mt-3 text-sm"><p className="font-semibold text-brand-green text-xs uppercase tracking-wide">History</p>
                    <ul className="mt-1 space-y-1">{selected.treatment_history.map((t, i) => <li key={`${t.at}-${i}`} className="text-brand-muted">• {t.note} <span className="text-xs">({new Date(t.at).toLocaleString()})</span></li>)}</ul>
                  </div>
                )}

                <div className="flex justify-end gap-3 mt-5">
                  <Button variant="outline" onClick={() => setSelected(null)} className="rounded-full">Close</Button>
                  <Button data-testid="admin-save-btn" onClick={save} disabled={saving} className="rounded-full bg-brand-green text-white hover:bg-brand-greenHover">{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Save className="h-4 w-4 mr-2" /> Save Changes</>}</Button>
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
